import { alpha, createTheme } from '@mui/material/styles';
import { liftangoBrandPalette } from './brandTokens';

declare module '@mui/material/styles' {
  interface Palette {
    liftango: {
      orange: string;
      tarmac: string;
      tarmacDark: string;
    };
    surface: {
      canvas: string;
      primary: string;
      secondary: string;
      muted: string;
      elevated: string;
      borderSubtle: string;
    };
    actionBrand: {
      primary: string;
      primaryHover: string;
      secondary: string;
      secondaryHover: string;
      inverted: string;
      outlinedBorder: string;
    };
  }

  interface PaletteOptions {
    liftango?: {
      orange: string;
      tarmac: string;
      tarmacDark: string;
    };
    surface?: {
      canvas: string;
      primary: string;
      secondary: string;
      muted: string;
      elevated: string;
      borderSubtle: string;
    };
    actionBrand?: {
      primary: string;
      primaryHover: string;
      secondary: string;
      secondaryHover: string;
      inverted: string;
      outlinedBorder: string;
    };
  }

  interface Theme {
    customLinks: {
      default: string;
      hover: string;
      visited: string;
    };
  }

  interface ThemeOptions {
    customLinks?: {
      default?: string;
      hover?: string;
      visited?: string;
    };
  }
}

export const liftangoTheme = createTheme({
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
    h1: { fontWeight: 800 },
    h2: { fontWeight: 800 },
    h3: { fontWeight: 800 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
  },
  palette: {
    mode: 'light',
    primary: {
      main: liftangoBrandPalette.primary,
      dark: liftangoBrandPalette.primaryDark,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: liftangoBrandPalette.secondary,
      contrastText: '#1F2937',
    },
    liftango: {
      orange: liftangoBrandPalette.primary,
      tarmac: liftangoBrandPalette.tertiary,
      tarmacDark: '#2C2C36',
    },
    background: {
      default: liftangoBrandPalette.backgroundDefault,
      paper: liftangoBrandPalette.backgroundPaper,
    },
    surface: {
      canvas: liftangoBrandPalette.backgroundDefault,
      primary: liftangoBrandPalette.backgroundPaper,
      secondary: '#F7F7F8',
      muted: '#ECECF0',
      elevated: '#FFFFFF',
      borderSubtle: 'rgba(58, 58, 72, 0.12)',
    },
    actionBrand: {
      primary: liftangoBrandPalette.primary,
      primaryHover: liftangoBrandPalette.primaryDark,
      secondary: '#2C2C36',
      secondaryHover: '#202029',
      inverted: '#3A3A48',
      outlinedBorder: 'rgba(58, 58, 72, 0.24)',
    },
    text: {
      primary: liftangoBrandPalette.textPrimary,
      secondary: liftangoBrandPalette.textSecondary,
    },
    success: {
      main: liftangoBrandPalette.success,
      contrastText: '#0F172A',
    },
    warning: {
      main: liftangoBrandPalette.warning,
      contrastText: '#1F2937',
    },
    error: {
      main: liftangoBrandPalette.error,
      contrastText: '#FFFFFF',
    },
  },
  customLinks: {
    default: liftangoBrandPalette.linkDefault,
    hover: liftangoBrandPalette.linkHover,
    visited: liftangoBrandPalette.linkVisited,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        body: {
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
        },
        a: {
          color: theme.customLinks.default,
          textDecorationColor: theme.customLinks.default,
        },
        'a:hover': {
          color: theme.customLinks.hover,
          textDecorationColor: theme.customLinks.hover,
        },
        'a:visited': {
          color: theme.customLinks.visited,
          textDecorationColor: theme.customLinks.visited,
        },
      }),
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          fontWeight: 600,
        },
        containedPrimary: {
          backgroundColor: alpha(liftangoBrandPalette.primary, 0.9),
          '&:hover': {
            backgroundColor: alpha(liftangoBrandPalette.primaryDark, 0.9),
          },
        },
        outlinedPrimary: {
          backgroundColor: alpha('#FFFFFF', 0.72),
        },
      },
    },
  },
  shape: {
    borderRadius: 12,
  },
});
